import { ChevronDown, ChevronRight } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useFixedMenuPosition } from '../../hooks/useFixedMenuPosition'
import {
  filterSelectableDepartmentTree,
  getDepartmentPathLabel,
} from '../../lib/department-tree'
import { cn } from '../../lib/utils'
import type { Department } from '../../types/department'

const EMPTY_EXCLUDED_IDS = new Set<string>()

type DepartmentParentPickerProps = {
  tree: Department[]
  value: string
  onChange: (parentId: string) => void
  excludedIds?: Set<string>
  /** When false, user must pick a department (signup). Default true for parent selection. */
  showNoneOption?: boolean
  placeholder?: string
  /** Label for the clear/none row when showNoneOption is true. */
  noneLabel?: string
  disabled?: boolean
  /** Optional helper shown above the trigger (signup / case form copy). */
  hint?: string
}

type TreeRowsProps = {
  nodes: Department[]
  depth: number
  hoverPath: string[]
  selectedId: string
  onHover: (depth: number, nodeId: string) => void
  onSelect: (nodeId: string) => void
}

function TreeRows({ nodes, depth, hoverPath, selectedId, onHover, onSelect }: TreeRowsProps) {
  return (
    <>
      {nodes.map((node) => {
        const children = node.children ?? []
        const hasChildren = children.length > 0
        const isHoveredPath = hoverPath[depth] === node.id
        const isSelected = selectedId === node.id
        const showChildren = hasChildren && isHoveredPath

        return (
          <div key={node.id}>
            <button
              type="button"
              className={cn(
                'flex w-full items-center gap-1 py-2 pr-3 text-left text-sm transition-colors',
                isHoveredPath || isSelected
                  ? 'bg-green-50 text-green-800'
                  : 'text-ink-800 hover:bg-parch-50',
              )}
              style={{ paddingLeft: `${0.75 + depth * 0.875}rem` }}
              onMouseEnter={() => onHover(depth, node.id)}
              onClick={() => onSelect(node.id)}
            >
              {hasChildren ? (
                <ChevronRight
                  className={cn(
                    'h-4 w-4 shrink-0 text-ink-400 transition-transform',
                    showChildren && 'rotate-90',
                  )}
                />
              ) : (
                <span className="inline-block h-4 w-4 shrink-0" />
              )}
              <span className="min-w-0 flex-1 truncate">{node.name}</span>
            </button>

            {showChildren ? (
              <TreeRows
                nodes={children}
                depth={depth + 1}
                hoverPath={hoverPath}
                selectedId={selectedId}
                onHover={onHover}
                onSelect={onSelect}
              />
            ) : null}
          </div>
        )
      })}
    </>
  )
}

export function DepartmentParentPicker({
  tree,
  value,
  onChange,
  excludedIds = EMPTY_EXCLUDED_IDS,
  showNoneOption = true,
  placeholder = 'None (top level)',
  noneLabel,
  disabled = false,
  hint,
}: DepartmentParentPickerProps) {
  const rootRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)

  const [open, setOpen] = useState(false)
  const [hoverPath, setHoverPath] = useState<string[]>([])

  const menuStyle = useFixedMenuPosition(open && !disabled, triggerRef)

  const selectableTree = useMemo(
    () => filterSelectableDepartmentTree(tree, excludedIds),
    [tree, excludedIds],
  )

  const selectedLabel = value
    ? getDepartmentPathLabel(selectableTree, value) ?? 'Selected department'
    : placeholder

  useEffect(() => {
    if (!open) {
      return
    }

    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node

      if (rootRef.current?.contains(target) || menuRef.current?.contains(target)) {
        return
      }

      setOpen(false)
      setHoverPath([])
    }

    document.addEventListener('mousedown', handlePointerDown)
    return () => document.removeEventListener('mousedown', handlePointerDown)
  }, [open])

  const handleHover = (depth: number, nodeId: string) => {
    setHoverPath((current) => [...current.slice(0, depth), nodeId])
  }

  const handleSelect = (parentId: string) => {
    onChange(parentId)
    setOpen(false)
    setHoverPath([])
  }

  return (
    <div ref={rootRef} className="relative">
      {hint ? <p className="mb-2 text-xs text-ink-500">{hint}</p> : null}
      <button
        ref={triggerRef}
        type="button"
        disabled={disabled}
        className={cn(
          'flex w-full items-center justify-between rounded-md border border-parch-200 bg-white px-3 py-2 text-left text-sm text-ink-900',
          disabled ? 'cursor-not-allowed opacity-60' : 'hover:bg-parch-50',
        )}
        onClick={() => {
          if (disabled) {
            return
          }

          if (open) {
            setOpen(false)
            setHoverPath([])
            return
          }

          setOpen(true)
        }}
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-disabled={disabled}
      >
        <span className="truncate">{selectedLabel}</span>
        <ChevronDown
          className={cn('h-4 w-4 shrink-0 text-ink-400 transition-transform', open && 'rotate-180')}
        />
      </button>

      {open &&
        !disabled &&
        createPortal(
          <div
            ref={menuRef}
            style={menuStyle}
            className="max-h-72 overflow-y-auto rounded-md border border-parch-200 bg-white py-1 shadow-lg"
            role="listbox"
            onMouseLeave={() => setHoverPath([])}
          >
            {showNoneOption && (
              <button
                type="button"
                className={cn(
                  'flex w-full px-3 py-2 text-left text-sm transition-colors',
                  value === '' ? 'bg-green-50 text-green-800' : 'text-ink-800 hover:bg-parch-50',
                )}
                onMouseEnter={() => setHoverPath([])}
                onClick={() => handleSelect('')}
              >
                {noneLabel ?? placeholder}
              </button>
            )}

            <TreeRows
              nodes={selectableTree}
              depth={0}
              hoverPath={hoverPath}
              selectedId={value}
              onHover={handleHover}
              onSelect={handleSelect}
            />
          </div>,
          document.body,
        )}
    </div>
  )
}
